import { MarketingFeatureGrid } from '@/components/marketing/blocks/marketing-feature-grid'
import { MarketingSplitNarrative } from '@/components/marketing/blocks/marketing-split-narrative'
import { MarketingStepsRow } from '@/components/marketing/blocks/marketing-steps-row'
import { MarketingWaitlistBand } from '@/components/marketing/blocks/marketing-waitlist-band'
import { MarketingButton } from '@/components/marketing/marketing-button'
import { MARKETING_SECTION_TITLE } from '@/components/marketing/marketing-typography'
import { WaitlistFormShell } from '@/components/marketing/waitlist-form-shell'
import type { LandingFeatureItem, LandingSection } from '@/types/client-landing'
import { cn } from '@/lib/utils'

export type MarketingLandingSectionRendererProps = {
  section: LandingSection
}

function normalizeFeatureItem(item: string | LandingFeatureItem): LandingFeatureItem {
  if (typeof item === 'string') return { title: item }
  return item
}

export function MarketingLandingSectionRenderer({ section }: MarketingLandingSectionRendererProps) {
  switch (section.type) {
    case 'features': {
      const items = section.props.items.map((raw) => {
        const item = normalizeFeatureItem(raw)
        return {
          icon: item.icon ?? '◎',
          title: item.title,
          description: item.description ?? '',
        }
      })
      return (
        <MarketingFeatureGrid
          title={section.props.title ?? ''}
          lead={section.props.lead}
          items={items}
        />
      )
    }
    case 'steps': {
      const steps = section.props.steps.map((step, i) => ({
        number: String(i + 1).padStart(2, '0'),
        title: step.title,
        description: step.description ?? '',
      }))
      return (
        <div>
          {section.props.title ? (
            <h2 className={cn('m-0 mb-[18px] max-w-[800px]', MARKETING_SECTION_TITLE)}>
              {section.props.title}
            </h2>
          ) : null}
          <MarketingStepsRow steps={steps} />
        </div>
      )
    }
    case 'ctaBand':
      return (
        <div className="text-center">
          {section.props.title ? (
            <h2 className={cn('m-0 mb-[18px]', MARKETING_SECTION_TITLE)}>{section.props.title}</h2>
          ) : null}
          {section.props.description ? (
            <p className="mx-auto mb-8 max-w-[760px] text-lg leading-relaxed text-[var(--marketing-muted,#667078)]">
              {section.props.description}
            </p>
          ) : null}
          <MarketingButton href={section.props.cta.to}>{section.props.cta.label}</MarketingButton>
        </div>
      )
    case 'splitNarrative':
      return (
        <MarketingSplitNarrative
          title={section.props.title}
          lead={section.props.lead}
          cta={section.props.cta}
        />
      )
    case 'waitlistBand':
      return (
        <MarketingWaitlistBand title={section.props.title} lead={section.props.lead}>
          <div className="mt-8">
            <WaitlistFormShell />
          </div>
        </MarketingWaitlistBand>
      )
    case 'quote':
      return (
        <blockquote className="mx-auto max-w-[800px] text-center">
          <p className="text-lg leading-relaxed text-[var(--marketing-text,#1f2528)] md:text-xl">
            &ldquo;{section.props.quote}&rdquo;
          </p>
          {section.props.attribution ? (
            <footer className="mt-3 text-sm text-[var(--marketing-muted,#667078)]">
              — {section.props.attribution}
            </footer>
          ) : null}
        </blockquote>
      )
    case 'logos':
      return (
        <div>
          {section.props.title ? (
            <h2 className={cn('m-0 mb-[18px] max-w-[800px]', MARKETING_SECTION_TITLE)}>
              {section.props.title}
            </h2>
          ) : null}
          <div className="flex flex-wrap gap-3">
            {section.props.captions.map((caption) => (
              <span
                key={caption}
                className="rounded-full border border-[var(--marketing-border,#e9e3d6)] bg-[var(--marketing-card,#ffffff)] px-4 py-2 text-sm text-[var(--marketing-text,#1f2528)]"
              >
                {caption}
              </span>
            ))}
          </div>
        </div>
      )
    default:
      return null
  }
}

/** Section band wrapper metadata shared by JSON blocks when `surface` is marketing. */
export function getMarketingSectionBandProps(section: LandingSection): {
  id?: string
  variant?: 'cream' | 'default'
} {
  if ('props' in section && section.props && typeof section.props === 'object') {
    const props = section.props as { id?: string; variant?: 'cream' | 'default' }
    return { id: props.id, variant: props.variant }
  }
  return {}
}
